// frozen_string_literal: true

class HealthMonitor {
  constructor() {
    this.isServerDown = false;
    this.checkInterval = 15000; // 15 seconds
    this.healthCheckTimer = null;
    this.googleAppsScriptUrl = null;
    this.currentUser = null;
    
    this.init();
  }

  init() {
    // Add CSS animations
    this.addAnimations();
    
    // Get Google Apps Script URL from Rails config
    this.loadConfig();
    
    // Start health monitoring
    this.startHealthCheck();
    
    // Handle form submissions
    this.interceptSupportCaseSubmissions();
    
    // Add fallback button for development/testing
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      this.addFallbackButton();
      // Hide button initially if server is up
      if (!this.isServerDown) {
        this.hideFallbackButton();
      }
    }
  }

  addAnimations() {
    // Add CSS animations for health monitor components
    if (!document.getElementById('health-monitor-animations')) {
      const style = document.createElement('style');
      style.id = 'health-monitor-animations';
      style.textContent = `
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        
        @keyframes slideIn {
          from { 
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes slideInRight {
          from { 
            opacity: 0;
            transform: translateX(100px);
          }
          to { 
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .health-monitor-backdrop {
          backdrop-filter: blur(4px);
        }
      `;
      document.head.appendChild(style);
    }
  }

  loadConfig() {
    // Try to get the Google Apps Script URL from a meta tag
    const metaTag = document.querySelector('meta[name="google-apps-script-url"]');
    if (metaTag) {
      this.googleAppsScriptUrl = metaTag.content;
    }
    
    
    // Get current user info if available
    const userMetaTag = document.querySelector('meta[name="current-user"]');
    if (userMetaTag) {
      try {
        this.currentUser = JSON.parse(userMetaTag.content);
      } catch (e) {
        // Silent fail for user data parsing
      }
    }
  }

  async checkServerHealth() {
    try {
      const response = await fetch('/up', {
        method: 'GET',
        cache: 'no-cache',
        timeout: 5000
      });
      
      if (response.ok) {
        if (this.isServerDown) {
          this.onServerRestored();
        }
        this.isServerDown = false;
        // Hide fallback button when server is up
        this.hideFallbackButton();
      } else {
        throw new Error('Server responded with error status');
      }
    } catch (error) {
      if (!this.isServerDown) {
        this.onServerDown();
      }
      this.isServerDown = true;
      // Show fallback button when server is down
      this.showFallbackButton();
    }
  }

  startHealthCheck() {
    // Initial check
    this.checkServerHealth();
    
    // Set up periodic checks
    this.healthCheckTimer = setInterval(() => {
      this.checkServerHealth();
    }, this.checkInterval);
  }

  stopHealthCheck() {
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer);
      this.healthCheckTimer = null;
    }
  }

  onServerDown() {
    // Add visual indicator if needed
    this.showServerDownIndicator();
  }

  onServerRestored() {
    // Remove visual indicator
    this.hideServerDownIndicator();
  }

  showServerDownIndicator() {
    // Add a subtle indicator that we're in fallback mode
    const indicator = document.createElement('div');
    indicator.id = 'server-status-indicator';
    indicator.className = 'fixed top-0 left-0 right-0 bg-gradient-to-r from-orange-400 to-amber-400 border-b border-orange-300 px-4 py-3 text-sm text-white text-center z-40 shadow-md';
    indicator.innerHTML = `
      <div class="flex items-center justify-center space-x-2">
        <svg class="w-4 h-4 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
        <span class="font-medium">System Maintenance</span>
        <span class="hidden sm:inline">- Support requests will be processed through our backup system</span>
      </div>
    `;
    
    if (!document.getElementById('server-status-indicator')) {
      document.body.insertBefore(indicator, document.body.firstChild);
    }
  }

  hideServerDownIndicator() {
    const indicator = document.getElementById('server-status-indicator');
    if (indicator) {
      indicator.remove();
    }
  }


  interceptSupportCaseSubmissions() {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      const isNewSupportCaseForm = form.action && form.action.includes('/support_cases') && 
                                  form.method.toLowerCase() === 'post';
      
      if (isNewSupportCaseForm && this.isServerDown) {
        event.preventDefault();
        this.showFallbackForm();
      }
    });
  }

  addFallbackButton() {
    // Add a simple button to test the fallback form
    const button = document.createElement('button');
    button.id = 'fallback-form-button';
    button.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: #10b981;
      color: white;
      border: none;
      padding: 12px 24px;
      border-radius: 8px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      z-index: 9999;
    `;
    button.textContent = 'Support Form';
    button.onclick = () => this.showFallbackForm();
    document.body.appendChild(button);
  }

  hideFallbackButton() {
    const button = document.getElementById('fallback-form-button');
    if (button) {
      button.style.display = 'none';
    }
  }

  showFallbackButton() {
    const button = document.getElementById('fallback-form-button');
    if (button) {
      button.style.display = 'block';
    }
  }

  showFallbackForm() {
    // Create a simple fallback form
    const backdrop = document.createElement('div');
    backdrop.id = 'fallback-modal';
    backdrop.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      padding: 20px;
    `;
    
    const modal = document.createElement('div');
    modal.style.cssText = `
      background: white;
      border-radius: 12px;
      padding: 32px;
      max-width: 500px;
      width: 100%;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    `;
    
    modal.innerHTML = `
      <h2 style="font-size: 24px; font-weight: bold; margin-bottom: 24px; color: #111827;">Contact Support</h2>
      
      <form id="fallback-form" style="display: flex; flex-direction: column; gap: 20px;">
        <div>
          <label style="display: block; font-size: 14px; font-weight: 600; color: #374151; margin-bottom: 8px;">
            Email
          </label>
          <input type="email" name="email" required
                 style="width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 16px;"
                 placeholder="your@email.com">
        </div>
        
        <div>
          <label style="display: block; font-size: 14px; font-weight: 600; color: #374151; margin-bottom: 8px;">
            Name
          </label>
          <input type="text" name="name" required
                 style="width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 16px;"
                 placeholder="John Doe">
        </div>
        
        <div>
          <label style="display: block; font-size: 14px; font-weight: 600; color: #374151; margin-bottom: 8px;">
            Message
          </label>
          <textarea name="message" rows="4" required
                    style="width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 16px; resize: vertical;"
                    placeholder="How can we help you?"></textarea>
        </div>
        
        <div style="display: flex; gap: 12px; justify-content: flex-end; margin-top: 8px;">
          <button type="button" onclick="document.getElementById('fallback-modal').remove()"
                  style="padding: 10px 20px; border: 1px solid #d1d5db; background: white; border-radius: 6px; font-weight: 600; cursor: pointer;">
            Cancel
          </button>
          <button type="submit" id="submit-support-btn"
                  style="padding: 10px 20px; background: #10b981; color: white; border: none; border-radius: 6px; font-weight: 600; cursor: pointer;">
            Submit
          </button>
        </div>
      </form>
    `;
    
    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);
    
    // Handle form submission
    document.getElementById('fallback-form').addEventListener('submit', async (e) => {
      e.preventDefault();
      
      // Disable submit button and show loading state
      const submitBtn = document.getElementById('submit-support-btn');
      submitBtn.disabled = true;
      submitBtn.textContent = 'Submitting...';
      submitBtn.style.background = '#6b7280';
      submitBtn.style.cursor = 'not-allowed';
      
      const formData = new FormData(e.target);
      
      try {
        await this.submitFormToGoogle(formData);
        backdrop.remove();
      } catch (error) {
        // Re-enable button on error
        submitBtn.disabled = false;
        submitBtn.textContent = 'Submit';
        submitBtn.style.background = '#10b981';
        submitBtn.style.cursor = 'pointer';
      }
    });
    
    // Close on backdrop click
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) {
        backdrop.remove();
      }
    });
  }

  async submitFormToGoogle(formData) {
    if (!this.googleAppsScriptUrl) {
      alert('Google Apps Script URL not configured');
      return;
    }
    
    try {
      const payload = {
        email: formData.get('email'),
        name: formData.get('name'),
        message: formData.get('message'),
        timestamp: new Date().toISOString()
      };
      
      
      await fetch(this.googleAppsScriptUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      
      // Show success message
      const successDiv = document.createElement('div');
      successDiv.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #10b981;
        color: white;
        padding: 16px 24px;
        border-radius: 8px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        z-index: 10001;
      `;
      successDiv.textContent = 'Message sent successfully!';
      document.body.appendChild(successDiv);
      
      setTimeout(() => successDiv.remove(), 3000);
      
    } catch (error) {
      alert('Failed to send message. Please try again.');
    }
  }


  showFallbackSupportForm() {
    // Create modal backdrop
    const backdrop = document.createElement('div');
    backdrop.id = 'support-fallback-modal';
    backdrop.className = 'fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm';
    backdrop.style.animation = 'fadeIn 0.3s ease-out';
    
    // Create modal content
    const modal = document.createElement('div');
    modal.className = 'bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100';
    modal.style.animation = 'slideIn 0.3s ease-out';
    
    const currentUserEmail = this.currentUser?.email || '';
    
    modal.innerHTML = `
      <div class="p-8">
        <div class="flex justify-between items-center mb-6">
          <div>
            <h2 class="text-2xl font-bold text-gray-900 mb-1">Contact Support</h2>
            <p class="text-sm text-gray-600">We're here to help you with any questions or issues</p>
          </div>
          <button id="close-support-modal" class="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-2 transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <div class="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4 mb-6">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-orange-500" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-semibold text-orange-800 mb-1">Backup Support System Active</h3>
              <p class="text-sm text-orange-700">
                We're currently experiencing technical difficulties. Your message will be submitted through our secure backup system and we'll get back to you as soon as possible.
              </p>
            </div>
          </div>
        </div>
        
        <form id="fallback-support-form" class="space-y-6">
          ${!currentUserEmail ? `
            <div>
              <label for="fallback-email" class="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
              <input type="email" id="fallback-email" name="email" required 
                     class="block w-full px-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                     placeholder="your.email@example.com">
            </div>
          ` : `
            <input type="hidden" name="email" value="${currentUserEmail}">
            <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div class="flex items-center space-x-2">
                <svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
                <span class="text-sm font-medium text-gray-700">Sending as: ${currentUserEmail}</span>
              </div>
            </div>
          `}
          
          <div>
            <label for="fallback-name" class="block text-sm font-semibold text-gray-700 mb-2">Name</label>
            <input type="text" id="fallback-name" name="name" required 
                   class="block w-full px-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                   placeholder="Your full name">
          </div>
          
          <div>
            <label for="fallback-body" class="block text-sm font-semibold text-gray-700 mb-2">Message</label>
            <textarea id="fallback-body" name="body" rows="6" required 
                      class="block w-full px-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 resize-none"
                      placeholder="Please describe your issue, question, or feedback in detail. The more information you provide, the better we can help you."></textarea>
          </div>
          
          <div class="flex justify-end space-x-3 pt-4 border-t border-gray-100">
            <button type="button" id="cancel-support-modal" 
                    class="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-200">
              Cancel
            </button>
            <button type="submit" 
                    class="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 border border-transparent rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-200 shadow-md">
              Send Message
            </button>
          </div>
        </form>
      </div>
    `;
    
    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);
    
    // Add event listeners
    document.getElementById('close-support-modal').addEventListener('click', () => {
      this.closeFallbackModal();
    });
    
    document.getElementById('cancel-support-modal').addEventListener('click', () => {
      this.closeFallbackModal();
    });
    
    backdrop.addEventListener('click', (event) => {
      if (event.target === backdrop) {
        this.closeFallbackModal();
      }
    });
    
    document.getElementById('fallback-support-form').addEventListener('submit', (event) => {
      event.preventDefault();
      this.submitToGoogleAppsScript(new FormData(event.target));
    });
  }

  closeFallbackModal() {
    const modal = document.getElementById('support-fallback-modal');
    if (modal) {
      modal.remove();
    }
  }

  async handleFallbackSubmission(form) {
    const formData = new FormData(form);
    await this.submitToGoogleAppsScript(formData);
  }

  async submitToGoogleAppsScript(formData) {
    if (!this.googleAppsScriptUrl) {
      alert('Backup submission system is not available. Please try again later or contact help@inloop.studio directly.');
      return;
    }
    
    // Disable submit button
    const submitButton = document.querySelector('#fallback-support-form button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Submitting...';
    }
    
    try {
      const payload = {
        type: 'support_case',
        email: formData.get('email'),
        name: formData.get('name'),
        message: formData.get('body'), // Map 'body' field to 'message' for Google
        timestamp: new Date().toISOString(),
        fallback_submission: true
      };
      
      await fetch(this.googleAppsScriptUrl, {
        method: 'POST',
        mode: 'no-cors', // Google Apps Script requires this
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      
      // Since we're using no-cors, we can't check the response status
      // We'll assume success and show success message
      this.showSuccessMessage();
      this.closeFallbackModal();
      
    } catch (error) {
      console.error('Failed to submit to Google Apps Script:', error);
      alert('Failed to submit your message. Please try again or contact help@inloop.studio directly.');
      
      // Re-enable submit button
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = 'Submit';
      }
    }
  }

  showSuccessMessage() {
    // Create success notification
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-4 rounded-xl shadow-2xl z-50 border border-green-400';
    notification.style.animation = 'slideInRight 0.3s ease-out';
    notification.innerHTML = `
      <div class="flex items-center space-x-3">
        <div class="flex-shrink-0">
          <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div>
          <div class="font-semibold">Message Sent Successfully!</div>
          <div class="text-sm text-green-100">We'll get back to you as soon as possible.</div>
        </div>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Remove notification after 5 seconds with fade out
    setTimeout(() => {
      notification.style.animation = 'fadeOut 0.3s ease-out';
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 5000);
  }

}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.healthMonitor = new HealthMonitor();
  });
} else {
  // DOM is already ready
  window.healthMonitor = new HealthMonitor();
}

// Clean up on page unload
window.addEventListener('beforeunload', () => {
  if (window.healthMonitor) {
    window.healthMonitor.stopHealthCheck();
  }
});

export default HealthMonitor;